import type { LinkToCaseResponse } from '@compass/client/types/case_record';
import { caseRecordService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Hook to link a case record to an existing case.
 * This triggers the PQC workflow directly at intake_draft (skipping transcription).
 *
 * Note: This hook does NOT show error toasts — callers (e.g. CreateCase handleSubmit)
 * are responsible for error presentation to avoid duplicate toasts.
 */
export const useLinkToCase = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation<LinkToCaseResponse, Error, { caseRecordId: string; workflowId?: string }>({
    mutationFn: async ({ caseRecordId, workflowId }) => {
      if (!selectedOrganizationId || !selectedProgramId) {
        throw new Error('Organization and program must be selected');
      }
      return caseRecordService.linkToCase({
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        case_record_id: caseRecordId,
        workflow_id: workflowId,
      });
    },
    onSuccess: () => {
      // Invalidate all case queries (includes specific case records and lists)
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.all,
      });
    },
  });
};
